import type { PinType } from './analytics';
import { trackEvent } from './analytics';

export type ShareMethod = 'copy_link' | 'whatsapp';

const WHATSAPP_MESSAGES: Record<PinType, string> = {
  spot: 'Check out this parking spot on parksy.rent:',
  seeker: 'Looking for parking near here on parksy.rent:',
  rent: 'See what people pay for parking here on parksy.rent:',
};

export function buildShareUrl(type: PinType, id: string): string {
  if (typeof window === 'undefined') return `/p/${type}/${id}`;
  return `${window.location.origin}/p/${type}/${id}`;
}

async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
    }
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

export async function copyShareLink(type: PinType, id: string): Promise<boolean> {
  const url = buildShareUrl(type, id);
  const ok = await copyToClipboard(url);
  trackEvent('share_clicked', { pin_type: type, share_method: 'copy_link' });
  return ok;
}

export function shareOnWhatsApp(type: PinType, id: string): void {
  const url = buildShareUrl(type, id);
  const text = `${WHATSAPP_MESSAGES[type]} ${url}`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
  window.open(waUrl, '_blank', 'noopener,noreferrer');
  trackEvent('share_clicked', { pin_type: type, share_method: 'whatsapp' });
}

export function attachShareClickHandler(root: ParentNode = document): void {
  root.addEventListener('click', (e: Event) => {
    const target = (e.target as HTMLElement | null)?.closest<HTMLElement>('button[data-share]');
    if (!target) return;
    const method = target.getAttribute('data-share-method') as ShareMethod | null;
    const pinType = target.getAttribute('data-pin-type') as PinType | null;
    const pinId = target.getAttribute('data-pin-id');
    if (!method || !pinType || !pinId) return;
    if (!['copy_link', 'whatsapp'].includes(method)) return;
    if (!['spot', 'seeker', 'rent'].includes(pinType)) return;

    if (method === 'copy_link') {
      copyShareLink(pinType, pinId);
    } else if (method === 'whatsapp') {
      shareOnWhatsApp(pinType, pinId);
    }
  });
}
